"use client";

import React from "react";
import { CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import rawData from "./avstreamPlans.json";
interface PlanLimits {
  users: string;
  calls: string;
  storage: string;
}
interface Plan {
  id: string;
  name: string;
  price: string;
  period: string;
  badge: string | null;
  limits: PlanLimits;
  bullets: string[];
}
interface AvStreamData {
  source?: string;
  currency?: string;
  period?: string;
  plans: Plan[];
}
const data = rawData as unknown as AvStreamData;
const features = ["De 10 hasta 200 usuarios / extensiones según el plan", "De 4 hasta 64 llamadas concurrentes de audio/video", "De 1 GB hasta 10 GB de almacenamiento en la nube", "App Wave para escritorio, móvil y web", "SBC integrado sin costo", "Funciones UC completas y grabación", "Soporte para plataformas de atención al cliente", "Integración completa de API y complementos de terceros", "Dominio y logo personalizados, y SDK de comunicación y colaboración en planes Pro y Business"];
export default function CloudHybridArchitecture() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const startupPlan = data.plans?.find(p => p.id === "startup") || data.plans?.[0];
  const businessPlan = data.plans?.find(p => p.id === "business") || data.plans?.[data.plans.length - 1];
  const startupPrice = startupPlan?.price || "$159";
  const businessPrice = businessPlan?.price || "$2099";
  return <section id="cloud-hybrid-architecture" className="bg-background py-16 md:py-24 overflow-hidden" ref={sectionRef}>
      <div className="container mx-auto px-4">
        <div className={`bg-foreground text-background rounded-3xl p-8 md:p-12 lg:p-16 shadow-2xl transition-all duration-1000 transform ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            
            {/* Left Column */}
            <div ref={leftRef} className={`space-y-8 transition-all duration-1000 delay-300 transform ${isLeftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
              <div className="flex flex-col items-center lg:items-start space-y-2">
                <span className="text-primary font-semibold text-sm tracking-wider uppercase">
                  Planes AvStream Connect
                </span>
                <h3 className="font-default text-xl md:text-2xl font-bold text-background">
                  Cloud UCM de Grandstream
                </h3>
              </div>
              
              <ul className="space-y-3">
                {features.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <CircleCheck className="w-5 h-5 text-background shrink-0 mt-0.5 opacity-80" />
                    <span className="text-sm md:text-base opacity-90 leading-tight">
                      {feature}
                    </span>
                  </li>)}
              </ul>
            </div>

            {/* Right Column */}
            <div ref={rightRef} className={`space-y-6 transition-all duration-1000 delay-500 transform ${isRightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
              <h2 className="font-default text-2xl md:text-3xl lg:text-4xl font-bold text-primary leading-tight">
                ELIGE EL PLAN QUE MEJOR SE AJUSTA A TU EMPRESA
              </h2>
              
              <div className="space-y-4 text-sm md:text-base opacity-90">
                <p>
                  Disponemos de cinco planes — <strong className="font-semibold text-background">Startup, SOHO, Plus, Pro y Business</strong> — todos construidos sobre la misma y potente plataforma de comunicaciones unificadas, diseñados para crecer contigo en <strong className="font-semibold text-background">usuarios, llamadas concurrentes y almacenamiento</strong>.
                </p>
                <p>
                  La inversión comienza desde <strong className="font-semibold text-background">{startupPrice} USD / año</strong> en el plan Startup (que incluye <strong className="font-semibold text-background">1 mes de prueba gratis</strong>) y escala hasta <strong className="font-semibold text-background">{businessPrice} USD / año</strong> en el plan Business, adaptándose con precisión al presupuesto de tu organización.
                </p>
                <p>
                  Además, puedes <strong className="font-semibold text-background">ampliar tu plan en cualquier momento</strong> mediante complementos y add-ons de extensiones adicionales, almacenamiento extra en la nube o personalización de marca sin necesidad de adquirir equipo físico adicional.
                </p>
              </div>

              <div className="pt-6">
                <Link to="#" className="block w-full">
                  <Button size="lg" className="w-full h-auto py-4 px-6 flex flex-col items-center text-center whitespace-normal bg-[#c4d65e] hover:bg-[#b3c54d] text-foreground font-bold text-sm md:text-base shadow-lg transition-transform hover:scale-[1.02]">
                    <span>Todo lo que tu empresa necesita para colaborar sin límites, sin</span>
                    <span>importar dónde estén tus equipos, AGENDA UNA LLAMADA!!!</span>
                  </Button>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>;
}